import { useEffect, useState } from 'react';
import type { MessageDTO, MessageVersionDTO } from '@clanfire/shared';
import { errorMessage } from '@/api/client';
import { messageApi } from '@/api/endpoints';
import { MessageContent } from '@/components/chat/MessageContent';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { formatLong } from '@/lib/time';
import { cn } from '@/lib/utils';

/**
 * What a message said before it was edited, newest first. Its author,
 * moderators, and the people in a DM can see this.
 */
export function EditHistoryModal({ message, onClose }: { message: MessageDTO; onClose: () => void }) {
  const [versions, setVersions] = useState<MessageVersionDTO[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    messageApi
      .versions(message.id)
      .then(setVersions)
      .catch((err) => setError(errorMessage(err)));
  }, [message.id]);

  const rows = versions
    ? [{ content: message.content, writtenAt: message.editedAt ?? message.createdAt, current: true }, ...[...versions].reverse().map((v) => ({ ...v, current: false }))]
    : [];

  return (
    <Modal open onClose={onClose} title="Edit history" description="What this message said before each edit. Up to 20 earlier versions are kept." size="md">
      {error ? (
        <p className="text-sm text-danger">{error}</p>
      ) : !versions ? (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      ) : (
        <ol className="flex flex-col gap-2">
          {rows.map((row, i) => (
            <li key={i} className={cn('rounded-xl border px-3 py-2', row.current ? 'border-accent/60 bg-accent-soft/50' : 'border-line bg-sidebar')}>
              <p className="mb-1 text-xs font-semibold text-fg-muted">
                {row.current ? 'Now' : i === rows.length - 1 ? 'Original' : 'Earlier'} · {formatLong(row.writtenAt)}
              </p>
              {row.content ? (
                <MessageContent content={row.content} serverId={message.serverId} className="text-sm leading-relaxed text-fg" />
              ) : (
                <p className="text-sm text-fg-muted italic">No text</p>
              )}
            </li>
          ))}
          {versions.length === 0 && <li className="text-sm text-fg-muted">No earlier versions were kept for this message.</li>}
        </ol>
      )}
    </Modal>
  );
}
